import React, { useState, useCallback, useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';

// Address type labels in Swedish
const addressTypeLabels = {
    billing: 'Fakturaadress',
    shipping: 'Leveransadress',
    invoice: 'Fakturaadress',
    other: 'Annan adress'
};

const CustomerSelectionCard = ({
    customers = [],
    selectedCustomer,
    onCustomerSelect,
    onFetchCustomerDetails,
    onCreateCustomer,
    loading = false,
    // Address and contact selection props
    selectedAddressId = null,
    selectedContactId = null,
    addressOverride = null,
    contactOverride = null,
    onAddressSelect,
    onContactSelect,
    onAddressOverride,
    onContactOverride,
    onResetAddressOverride,
    onResetContactOverride
}) => {
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [editedAddress, setEditedAddress] = useState({});
    const [editedContact, setEditedContact] = useState({});

    // Debug logging
    React.useEffect(() => {
        console.log('CustomerSelectionCard selectedCustomer changed:', selectedCustomer);
    }, [selectedCustomer]);

    // Format customer display based on type
    const getCustomerLabel = useCallback((customer) => {
        if (!customer) return '';

        if (customer.type === 'company') {
            const orgNumber = customer.organization_number || customer.orgnumber;
            return `${customer.name}${orgNumber ? ` (${orgNumber})` : ''}`;
        } else {
            const firstName = customer.first_name || customer.firstName || '';
            const lastName = customer.last_name || customer.lastName || '';
            const fullName = customer.name || `${firstName} ${lastName}`.trim();
            return fullName;
        }
    }, []);

    // Filter customers by search term
    const filterCustomers = useCallback((inputValue) => {
        const searchTerm = inputValue.toLowerCase();
        return customers.filter(customer => {
            const name = (customer.name || '').toLowerCase();
            const orgNumber = (customer.organization_number || customer.orgnumber || '').toLowerCase();
            const customerNumber = (customer.number || '').toString().toLowerCase();
            const email = (customer.email || '').toLowerCase();

            return name.includes(searchTerm) ||
                orgNumber.includes(searchTerm) ||
                customerNumber.includes(searchTerm) ||
                email.includes(searchTerm);
        });
    }, [customers]);

    // Load customer options for the async select
    const loadCustomerOptions = useCallback((inputValue) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const filteredCustomers = filterCustomers(inputValue);
                const options = filteredCustomers.slice(0, 50).map(customer => ({
                    value: customer.id,
                    label: getCustomerLabel(customer),
                    data: customer
                }));
                resolve(options);
            }, 300);
        });
    }, [filterCustomers, getCustomerLabel]);

    // Handle customer selection
    const handleSelectCustomer = useCallback(async (selectedOption) => {
        if (selectedOption) {
            const customer = customers.find(c => c.id === selectedOption.value);
            if (customer && onFetchCustomerDetails) {
                setIsLoadingDetails(true);
                try {
                    const fullCustomer = await onFetchCustomerDetails(customer.id);
                    onCustomerSelect(fullCustomer || customer);
                } catch (error) {
                    console.error('Error fetching customer details:', error);
                    onCustomerSelect(customer);
                } finally {
                    setIsLoadingDetails(false);
                }
            } else {
                onCustomerSelect(customer);
            }
        } else {
            onCustomerSelect(null);
        }
    }, [customers, onFetchCustomerDetails, onCustomerSelect]);

    // Get the currently active address (selected, overridden, or default)
    const getActiveAddress = useCallback((customer) => {
        if (addressOverride) return addressOverride;
        if (!customer?.addresses?.length) return null;

        if (selectedAddressId) {
            const selected = customer.addresses.find(a => a.address_id === selectedAddressId);
            if (selected) return selected;
        }

        // Default: first billing/invoice address or first available
        return customer.addresses.find(a => a.address_type === 'billing' || a.address_type === 'invoice')
            || customer.addresses[0];
    }, [selectedAddressId, addressOverride]);

    // Get the currently active contact (selected, overridden, or default)
    const getActiveContact = useCallback((customer) => {
        if (contactOverride) return contactOverride;
        if (!customer?.contacts?.length) return null;

        if (selectedContactId) {
            const selected = customer.contacts.find(c => c.contact_id === selectedContactId);
            if (selected) return selected;
        }

        // Default: first contact
        return customer.contacts[0];
    }, [selectedContactId, contactOverride]);

    // Check if customer has multiple addresses/contacts
    const hasMultipleAddresses = useMemo(() => {
        return selectedCustomer?.addresses?.length > 1;
    }, [selectedCustomer]);

    const hasMultipleContacts = useMemo(() => {
        return selectedCustomer?.contacts?.length > 1;
    }, [selectedCustomer]);

    const hasContacts = useMemo(() => {
        return selectedCustomer?.contacts?.length > 0;
    }, [selectedCustomer]);

    // Address options for dropdown
    const addressOptions = useMemo(() => {
        if (!selectedCustomer?.addresses) return [];
        return selectedCustomer.addresses.map(addr => ({
            value: addr.address_id,
            label: `${addressTypeLabels[addr.address_type] || 'Adress'} - ${addr.street1 || ''}, ${addr.city || ''}`,
            data: addr
        }));
    }, [selectedCustomer]);

    // Contact options for dropdown
    const contactOptions = useMemo(() => {
        if (!selectedCustomer?.contacts) return [];
        return selectedCustomer.contacts.map(contact => ({
            value: contact.contact_id,
            label: `${contact.contact_name}${contact.role ? ` (${contact.role})` : ''}`,
            data: contact
        }));
    }, [selectedCustomer]);

    // Handle address edit save
    const handleSaveAddressEdit = useCallback(() => {
        const activeAddr = getActiveAddress(selectedCustomer);
        const newOverride = {
            ...activeAddr,
            ...editedAddress
        };
        onAddressOverride?.(newOverride);
        setIsEditingAddress(false);
        setEditedAddress({});
    }, [selectedCustomer, editedAddress, getActiveAddress, onAddressOverride]);

    // Handle contact edit save
    const handleSaveContactEdit = useCallback(() => {
        const activeContact = getActiveContact(selectedCustomer);
        const newOverride = {
            ...activeContact,
            ...editedContact
        };
        onContactOverride?.(newOverride);
        setIsEditingContact(false);
        setEditedContact({});
    }, [selectedCustomer, editedContact, getActiveContact, onContactOverride]);

    // Cancel edit handlers
    const handleCancelAddressEdit = useCallback(() => {
        setIsEditingAddress(false);
        setEditedAddress({});
    }, []);

    const handleCancelContactEdit = useCallback(() => {
        setIsEditingContact(false);
        setEditedContact({});
    }, []);

    // Start editing with current values
    const handleStartAddressEdit = useCallback(() => {
        const activeAddr = getActiveAddress(selectedCustomer);
        setEditedAddress(activeAddr || {});
        setIsEditingAddress(true);
    }, [selectedCustomer, getActiveAddress]);

    const handleStartContactEdit = useCallback(() => {
        const activeContact = getActiveContact(selectedCustomer);
        setEditedContact(activeContact || {});
        setIsEditingContact(true);
    }, [selectedCustomer, getActiveContact]);

    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '48px',
            borderRadius: '8px',
            borderColor: state.isFocused ? 'var(--primary)' : 'var(--bs-light)',
            borderWidth: '1px',
            boxShadow: state.isFocused
                ? '0 0 0 3px var(--rgba-primary-1)'
                : 'none',
            '&:hover': {
                borderColor: 'var(--primary)',
            }
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '8px',
            border: '1px solid var(--bs-light)',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 1000
        }),
        option: (base, { isFocused, isSelected }) => ({
            ...base,
            backgroundColor: isSelected
                ? 'var(--primary)'
                : isFocused
                    ? 'var(--bs-light)'
                    : 'white',
            color: isSelected ? 'white' : 'var(--secondary)'
        })
    };

    const smallSelectStyles = {
        ...customSelectStyles,
        control: (base, state) => ({
            ...customSelectStyles.control(base, state),
            minHeight: '36px',
            fontSize: '0.875rem'
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 8px'
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0
        })
    };

    // Get active address and contact for rendering
    const activeAddress = getActiveAddress(selectedCustomer);
    const activeContact = getActiveContact(selectedCustomer);

    return (
        <>
            <style>{`
                .customer-selection-card {
                    background: white;
                    border-radius: 12px;
                    padding: 2rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid var(--bs-light);
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }

                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1.5rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid var(--bs-light);
                }

                .section-icon {
                    width: 40px;
                    height: 40px;
                    background: var(--rgba-primary-1);
                    border: 1px solid var(--primary);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--primary);
                    font-size: 1.25rem;
                }

                .section-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: var(--title);
                    margin: 0;
                }

                .section-subtitle {
                    font-size: 0.875rem;
                    color: var(--secondary);
                    margin: 0.25rem 0 0 0;
                }

                .search-section {
                    margin-bottom: 1.5rem;
                }

                .form-label-modern {
                    display: block;
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: var(--secondary);
                    margin-bottom: 0.5rem;
                }

                .required-asterisk {
                    color: var(--bs-danger);
                    margin-left: 0.25rem;
                }

                .form-text-modern {
                    margin-top: 0.5rem;
                    font-size: 0.75rem;
                    color: var(--secondary);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .loading-indicator {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    background: var(--rgba-primary-1);
                    border: 1px solid var(--primary);
                    border-radius: 8px;
                    color: var(--primary);
                    font-size: 0.875rem;
                    margin: 1rem 0;
                }

                .loading-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid var(--bs-light);
                    border-top: 2px solid var(--primary);
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .actions-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 1rem;
                }

                .btn-add-customer {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: var(--rgba-primary-1);
                    color: var(--primary);
                    border: 1px solid var(--primary);
                    border-radius: 6px;
                    font-size: 0.875rem;
                    font-weight: 500;
                    text-decoration: none;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }

                .btn-add-customer:hover {
                    background: var(--rgba-primary-2);
                    color: var(--primary-hover);
                    border-color: var(--primary-hover);
                    text-decoration: none;
                }

                .customer-count {
                    font-size: 0.75rem;
                    color: var(--secondary);
                    font-weight: 500;
                }

                /* Selected Customer Section */
                .selected-customer-section {
                    margin-top: 2rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid var(--bs-light);
                }

                .customer-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 1.5rem;
                }

                .customer-info {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                }

                .customer-avatar {
                    width: 48px;
                    height: 48px;
                    border-radius: 8px;
                    background: var(--rgba-primary-1);
                    border: 1px solid var(--primary);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--primary);
                    font-size: 1.25rem;
                    flex-shrink: 0;
                }

                .customer-details h3 {
                    font-size: 1.125rem;
                    font-weight: 600;
                    color: var(--title);
                    margin: 0 0 0.5rem 0;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .success-icon {
                    width: 16px;
                    height: 16px;
                    background: var(--primary);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 0.625rem;
                }

                .customer-type-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.75rem;
                    background: var(--rgba-primary-1);
                    color: var(--primary);
                    border: 1px solid var(--primary);
                    border-radius: 6px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }

                .btn-remove {
                    padding: 0.5rem;
                    background: var(--bs-light);
                    border: 1px solid var(--bs-light);
                    border-radius: 6px;
                    color: var(--secondary);
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-remove:hover {
                    background: rgba(247, 43, 80, 0.1);
                    border-color: rgba(247, 43, 80, 0.3);
                    color: var(--bs-danger);
                }

                .company-info {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                    padding-bottom: 1.5rem;
                    margin-bottom: 1.5rem;
                    border-bottom: 1px solid var(--bs-light);
                }

                .info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }

                .info-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: var(--secondary);
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }

                .info-value {
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: var(--title);
                }

                .contact-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 1.5rem;
                }

                .contact-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                }

                .contact-icon {
                    width: 32px;
                    height: 32px;
                    background: var(--rgba-primary-1);
                    border: 1px solid var(--primary);
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--primary);
                    font-size: 0.875rem;
                    flex-shrink: 0;
                    margin-top: 0.125rem;
                }

                .contact-details {
                    min-width: 0;
                    flex: 1;
                }

                .contact-details .info-label {
                    margin-bottom: 0.25rem;
                }

                .contact-details .info-value {
                    color: var(--primary);
                    text-decoration: none;
                    word-break: break-word;
                }

                .contact-details .info-value:hover {
                    color: var(--primary-hover);
                    text-decoration: underline;
                }

                .address-text {
                    line-height: 1.4;
                    color: var(--title) !important;
                }

                .address-text:hover {
                    text-decoration: none !important;
                }

                .customer-footer {
                    margin-top: 1.5rem;
                    padding-top: 1rem;
                    border-top: 1px solid var(--bs-light);
                }

                .customer-number {
                    font-size: 0.75rem;
                    color: var(--secondary);
                    font-weight: 500;
                }

                .customer-number strong {
                    color: var(--title);
                    font-weight: 600;
                }

                /* New styles for selectors and edit mode */
                .selector-section {
                    margin-bottom: 1rem;
                }

                .selector-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                }

                .selector-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: var(--secondary);
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }

                .override-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.125rem 0.5rem;
                    background: rgba(255, 170, 43, 0.1);
                    color: var(--bs-warning);
                    border: 1px solid var(--bs-warning);
                    border-radius: 4px;
                    font-size: 0.625rem;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .btn-reset {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.5rem;
                    background: transparent;
                    color: var(--secondary);
                    border: none;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-reset:hover {
                    background: var(--bs-light);
                    color: var(--primary);
                }

                .btn-edit-inline {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.5rem;
                    background: transparent;
                    color: var(--secondary);
                    border: 1px dashed var(--bs-light);
                    border-radius: 4px;
                    font-size: 0.75rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    margin-top: 0.5rem;
                }

                .btn-edit-inline:hover {
                    background: var(--rgba-primary-1);
                    border-color: var(--primary);
                    color: var(--primary);
                }

                .edit-form {
                    background: var(--bs-light);
                    border-radius: 8px;
                    padding: 1rem;
                    margin-top: 0.75rem;
                }

                .edit-form-header {
                    margin-bottom: 1rem;
                }

                .edit-form-title {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: var(--title);
                    margin: 0 0 0.25rem 0;
                }

                .edit-form-note {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    font-size: 0.75rem;
                    color: var(--secondary);
                    margin: 0;
                }

                .edit-form-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                    gap: 0.75rem;
                    margin-bottom: 1rem;
                }

                .edit-form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }

                .edit-form-group.full-width {
                    grid-column: 1 / -1;
                }

                .edit-form-label {
                    font-size: 0.75rem;
                    font-weight: 500;
                    color: var(--secondary);
                }

                .edit-form-input {
                    padding: 0.5rem 0.75rem;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    font-size: 0.875rem;
                    color: var(--title);
                    background: white;
                    transition: border-color 0.2s ease;
                }

                .edit-form-input:focus {
                    outline: none;
                    border-color: var(--primary);
                    box-shadow: 0 0 0 2px var(--rgba-primary-1);
                }

                .edit-form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.5rem;
                }

                .btn-cancel {
                    padding: 0.375rem 0.75rem;
                    background: white;
                    color: var(--secondary);
                    border: 1px solid var(--bs-light);
                    border-radius: 6px;
                    font-size: 0.75rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-cancel:hover {
                    background: var(--bs-light);
                }

                .btn-save {
                    padding: 0.375rem 0.75rem;
                    background: var(--primary);
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-size: 0.75rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-save:hover {
                    background: var(--primary-hover);
                }

                .contact-address-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.5rem;
                    margin-bottom: 1rem;
                }

                .contact-address-row:has(> :only-child) {
                    grid-template-columns: 1fr;
                }

                .contact-person-section {
                    background: var(--rgba-primary-1);
                    border: 1px solid var(--primary);
                    border-radius: 8px;
                    padding: 1rem;
                    display: flex;
                    flex-direction: column;
                }

                .address-section {
                    background: #f8fafc;
                    border: 1px solid var(--bs-light);
                    border-radius: 8px;
                    padding: 1rem;
                    display: flex;
                    flex-direction: column;
                }

                .contact-person-header {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-bottom: 0.5rem;
                }

                .contact-person-icon {
                    font-size: 1rem;
                    color: var(--primary);
                }

                .contact-person-title {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: var(--secondary);
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }

                .contact-person-name {
                    font-size: 1rem;
                    font-weight: 600;
                    color: var(--title);
                    margin: 0;
                }

                .contact-role-badge {
                    display: inline-flex;
                    padding: 0.125rem 0.5rem;
                    background: white;
                    color: var(--primary);
                    border-radius: 4px;
                    font-size: 0.6875rem;
                    font-weight: 500;
                    margin-left: 0.5rem;
                }

                .contact-person-details {
                    display: flex;
                    gap: 1rem;
                    margin-top: 0.5rem;
                    flex-wrap: wrap;
                }

                .contact-person-detail {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    font-size: 0.8125rem;
                    color: var(--secondary);
                }

                .contact-person-detail i {
                    color: var(--primary);
                }

                .contact-person-detail a {
                    color: var(--primary);
                    text-decoration: none;
                }

                .contact-person-detail a:hover {
                    text-decoration: underline;
                }

                @media (max-width: 768px) {
                    .customer-selection-card {
                        padding: 1.5rem;
                    }

                    .customer-header {
                        flex-direction: column;
                        gap: 1rem;
                        align-items: stretch;
                    }

                    .actions-row {
                        flex-direction: column;
                        gap: 0.75rem;
                        align-items: stretch;
                    }

                    .contact-grid {
                        grid-template-columns: 1fr;
                    }

                    .company-info {
                        grid-template-columns: 1fr;
                    }

                    .section-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                    }

                    .edit-form-grid {
                        grid-template-columns: 1fr;
                    }

                    .contact-address-row {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>

            <div className="customer-selection-card">
                {/* Section Header */}
                <div className="section-header">
                    <div className="section-icon">
                        <i className="bi bi-person"></i>
                    </div>
                    <div>
                        <h2 className="section-title">Kund</h2>
                        <p className="section-subtitle">
                            {selectedCustomer
                                ? `${selectedCustomer.name} är vald`
                                : 'Välj kund för fakturan'
                            }
                        </p>
                    </div>
                </div>

                {/* Search Section */}
                <div className="search-section">
                    <label className="form-label-modern">
                        {selectedCustomer ? 'Byt kund' : 'Sök och välj kund'}
                        <span className="required-asterisk">*</span>
                    </label>
                    <AsyncSelect
                        cacheOptions
                        defaultOptions={customers.slice(0, 20).map(customer => ({
                            value: customer.id,
                            label: getCustomerLabel(customer),
                            data: customer
                        }))}
                        value={selectedCustomer ? {
                            value: selectedCustomer.id,
                            label: getCustomerLabel(selectedCustomer)
                        } : null}
                        loadOptions={loadCustomerOptions}
                        onChange={handleSelectCustomer}
                        isClearable
                        placeholder={selectedCustomer ? "Sök för att byta kund..." : "Börja skriv för att söka kund..."}
                        noOptionsMessage={() => "Inga kunder hittades"}
                        loadingMessage={() => "Söker kunder..."}
                        isDisabled={loading || isLoadingDetails}
                        classNamePrefix="react-select"
                        styles={customSelectStyles}
                    />
                    <div className="form-text-modern">
                        <span>
                            {selectedCustomer
                                ? 'Sök med namn, organisationsnummer eller e-post för att byta kund'
                                : 'Sök med namn, organisationsnummer, kundnummer eller e-post'
                            }
                        </span>
                    </div>
                </div>

                {isLoadingDetails && (
                    <div className="loading-indicator">
                        <div className="loading-spinner"></div>
                        <span>Hämtar kunduppgifter...</span>
                    </div>
                )}

                <div className="actions-row">
                    <button
                        type="button"
                        className="btn-add-customer"
                        onClick={() => {
                            if (onCreateCustomer) {
                                onCreateCustomer();
                            } else {
                                console.log('Create new customer');
                            }
                        }}
                    >
                        <i className="bi bi-plus-lg"></i>
                        Skapa ny kund
                    </button>

                    <span className="customer-count">
                        {customers.length} kunder tillgängliga
                    </span>
                </div>

                {/* Selected Customer Display */}
                {selectedCustomer && (
                    <div className="selected-customer-section">
                        {/* Header */}
                        <div className="customer-header">
                            <div className="customer-info">
                                <div className="customer-avatar">
                                    <i className={`bi bi-${selectedCustomer.type === 'company' ? 'building' : 'person-fill'}`}></i>
                                </div>
                                <div className="customer-details">
                                    <h3>
                                        {selectedCustomer.name}
                                        <div className="success-icon">
                                            <i className="bi bi-check"></i>
                                        </div>
                                    </h3>
                                    <span className="customer-type-badge">
                                        <i className={`bi bi-${selectedCustomer.type === 'company' ? 'building' : 'person'}`}></i>
                                        {selectedCustomer.type === 'company' ? 'Företag' : 'Privatperson'}
                                    </span>
                                </div>
                            </div>

                            <button
                                className="btn-remove"
                                onClick={() => onCustomerSelect(null)}
                                title="Ta bort vald kund"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        {/* Company Info */}
                        {selectedCustomer.type === 'company' && selectedCustomer.orgnumber && (
                            <div className="company-info">
                                <div className="info-item">
                                    <div className="info-label">Organisationsnummer</div>
                                    <div className="info-value">{selectedCustomer.orgnumber}</div>
                                </div>
                                {selectedCustomer.vatnumber && (
                                    <div className="info-item">
                                        <div className="info-label">VAT-nummer</div>
                                        <div className="info-value">{selectedCustomer.vatnumber}</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Contact and Address Row - Side by Side */}
                        <div className="contact-address-row">
                            {/* Contact Person Section */}
                            {hasContacts && activeContact ? (
                                <div className="contact-person-section">
                                    <div className="contact-person-header">
                                        <i className="bi bi-person-badge contact-person-icon"></i>
                                        <span className="contact-person-title">Kontaktperson</span>
                                        {contactOverride && (
                                            <span className="override-badge">
                                                <i className="bi bi-pencil"></i> Anpassad
                                            </span>
                                        )}
                                    </div>

                                    {/* Contact selector if multiple */}
                                    {hasMultipleContacts && !isEditingContact && (
                                        <div className="selector-section">
                                            <Select
                                                options={contactOptions}
                                                value={contactOptions.find(opt =>
                                                    opt.value === (selectedContactId || activeContact?.contact_id)
                                                )}
                                                onChange={(opt) => onContactSelect?.(opt?.value)}
                                                styles={smallSelectStyles}
                                                placeholder="Välj kontaktperson..."
                                                isSearchable={false}
                                            />
                                        </div>
                                    )}

                                    {!isEditingContact ? (
                                        <>
                                            <p className="contact-person-name">
                                                {activeContact.contact_name}
                                                {activeContact.role && (
                                                    <span className="contact-role-badge">{activeContact.role}</span>
                                                )}
                                            </p>
                                            <div className="contact-person-details">
                                                {activeContact.contact_email && (
                                                    <div className="contact-person-detail">
                                                        <i className="bi bi-envelope"></i>
                                                        <a href={`mailto:${activeContact.contact_email}`}>
                                                            {activeContact.contact_email}
                                                        </a>
                                                    </div>
                                                )}
                                                {activeContact.contact_phone && (
                                                    <div className="contact-person-detail">
                                                        <i className="bi bi-telephone"></i>
                                                        <a href={`tel:${activeContact.contact_phone}`}>
                                                            {activeContact.contact_phone}
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                <button
                                                    className="btn-edit-inline"
                                                    onClick={handleStartContactEdit}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                    Redigera
                                                </button>
                                                {contactOverride && (
                                                    <button
                                                        className="btn-reset"
                                                        onClick={onResetContactOverride}
                                                    >
                                                        <i className="bi bi-arrow-counterclockwise"></i>
                                                        Återställ
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="edit-form">
                                            <div className="edit-form-header">
                                                <h4 className="edit-form-title">Redigera kontaktuppgifter</h4>
                                                <p className="edit-form-note">
                                                    <i className="bi bi-info-circle"></i>
                                                    Endast för denna faktura
                                                </p>
                                            </div>
                                            <div className="edit-form-grid">
                                                <div className="edit-form-group">
                                                    <label className="edit-form-label">Namn</label>
                                                    <input
                                                        type="text"
                                                        className="edit-form-input"
                                                        value={editedContact.contact_name || ''}
                                                        onChange={(e) => setEditedContact({
                                                            ...editedContact,
                                                            contact_name: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="edit-form-group">
                                                    <label className="edit-form-label">Roll</label>
                                                    <input
                                                        type="text"
                                                        className="edit-form-input"
                                                        value={editedContact.role || ''}
                                                        onChange={(e) => setEditedContact({
                                                            ...editedContact,
                                                            role: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="edit-form-group">
                                                    <label className="edit-form-label">E-post</label>
                                                    <input
                                                        type="email"
                                                        className="edit-form-input"
                                                        value={editedContact.contact_email || ''}
                                                        onChange={(e) => setEditedContact({
                                                            ...editedContact,
                                                            contact_email: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="edit-form-group">
                                                    <label className="edit-form-label">Telefon</label>
                                                    <input
                                                        type="tel"
                                                        className="edit-form-input"
                                                        value={editedContact.contact_phone || ''}
                                                        onChange={(e) => setEditedContact({
                                                            ...editedContact,
                                                            contact_phone: e.target.value
                                                        })}
                                                    />
                                                </div>
                                            </div>
                                            <div className="edit-form-actions">
                                                <button
                                                    className="btn-cancel"
                                                    onClick={handleCancelContactEdit}
                                                >
                                                    Avbryt
                                                </button>
                                                <button
                                                    className="btn-save"
                                                    onClick={handleSaveContactEdit}
                                                >
                                                    Spara
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : !hasContacts && (selectedCustomer.email || selectedCustomer.phone) ? (
                                /* Contact Information for customers without contacts array */
                                <div className="address-section">
                                    <div className="contact-person-header">
                                        <i className="bi bi-person contact-person-icon"></i>
                                        <span className="contact-person-title">Kontaktuppgifter</span>
                                    </div>
                                    {selectedCustomer.email && (
                                        <div className="contact-person-detail" style={{ marginBottom: '0.5rem' }}>
                                            <i className="bi bi-envelope"></i>
                                            <a href={`mailto:${selectedCustomer.email}`}>
                                                {selectedCustomer.email}
                                            </a>
                                        </div>
                                    )}
                                    {selectedCustomer.phone && (
                                        <div className="contact-person-detail">
                                            <i className="bi bi-telephone"></i>
                                            <a href={`tel:${selectedCustomer.phone}`}>
                                                {selectedCustomer.phone}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            ) : null}

                            {/* Address Section */}
                            {activeAddress && (
                                <div className="address-section">
                                    <div className="contact-person-header">
                                        <i className="bi bi-geo-alt contact-person-icon"></i>
                                        <span className="contact-person-title">
                                            {addressTypeLabels[activeAddress.address_type] || 'Adress'}
                                        </span>
                                        {addressOverride && (
                                            <span className="override-badge">
                                                <i className="bi bi-pencil"></i> Anpassad
                                            </span>
                                        )}
                                    </div>

                                    {/* Address selector if multiple */}
                                    {hasMultipleAddresses && !isEditingAddress && (
                                        <div className="selector-section">
                                            <Select
                                                options={addressOptions}
                                                value={addressOptions.find(opt =>
                                                    opt.value === (selectedAddressId || activeAddress?.address_id)
                                                )}
                                                onChange={(opt) => onAddressSelect?.(opt?.value)}
                                                styles={smallSelectStyles}
                                                placeholder="Välj adress..."
                                                isSearchable={false}
                                            />
                                        </div>
                                    )}

                                    {!isEditingAddress ? (
                                        <>
                                            <div className="info-value address-text">
                                                {activeAddress.street1 && <div>{activeAddress.street1}</div>}
                                                {activeAddress.street2 && <div>{activeAddress.street2}</div>}
                                                <div>
                                                    {(activeAddress.postal_code || activeAddress.postalcode) &&
                                                        `${activeAddress.postal_code || activeAddress.postalcode} `}
                                                    {activeAddress.city}
                                                </div>
                                                {activeAddress.country && activeAddress.country !== 'Sverige' && (
                                                    <div>{activeAddress.country}</div>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                <button
                                                    className="btn-edit-inline"
                                                    onClick={handleStartAddressEdit}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                    Redigera
                                                </button>
                                                {addressOverride && (
                                                    <button
                                                        className="btn-reset"
                                                        onClick={onResetAddressOverride}
                                                    >
                                                        <i className="bi bi-arrow-counterclockwise"></i>
                                                        Återställ
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="edit-form">
                                            <div className="edit-form-header">
                                                <h4 className="edit-form-title">Redigera adress</h4>
                                                <p className="edit-form-note">
                                                    <i className="bi bi-info-circle"></i>
                                                    Endast för denna faktura
                                                </p>
                                            </div>
                                            <div className="edit-form-grid">
                                                <div className="edit-form-group full-width">
                                                    <label className="edit-form-label">Gatuadress</label>
                                                    <input
                                                        type="text"
                                                        className="edit-form-input"
                                                        value={editedAddress.street1 || ''}
                                                        onChange={(e) => setEditedAddress({
                                                            ...editedAddress,
                                                            street1: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="edit-form-group full-width">
                                                    <label className="edit-form-label">Gatuadress 2</label>
                                                    <input
                                                        type="text"
                                                        className="edit-form-input"
                                                        value={editedAddress.street2 || ''}
                                                        onChange={(e) => setEditedAddress({
                                                            ...editedAddress,
                                                            street2: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="edit-form-group">
                                                    <label className="edit-form-label">Postnummer</label>
                                                    <input
                                                        type="text"
                                                        className="edit-form-input"
                                                        value={editedAddress.postal_code || editedAddress.postalcode || ''}
                                                        onChange={(e) => setEditedAddress({
                                                            ...editedAddress,
                                                            postal_code: e.target.value,
                                                            postalcode: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="edit-form-group">
                                                    <label className="edit-form-label">Ort</label>
                                                    <input
                                                        type="text"
                                                        className="edit-form-input"
                                                        value={editedAddress.city || ''}
                                                        onChange={(e) => setEditedAddress({
                                                            ...editedAddress,
                                                            city: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="edit-form-group">
                                                    <label className="edit-form-label">Land</label>
                                                    <input
                                                        type="text"
                                                        className="edit-form-input"
                                                        value={editedAddress.country || ''}
                                                        onChange={(e) => setEditedAddress({
                                                            ...editedAddress,
                                                            country: e.target.value
                                                        })}
                                                    />
                                                </div>
                                            </div>
                                            <div className="edit-form-actions">
                                                <button
                                                    className="btn-cancel"
                                                    onClick={handleCancelAddressEdit}
                                                >
                                                    Avbryt
                                                </button>
                                                <button
                                                    className="btn-save"
                                                    onClick={handleSaveAddressEdit}
                                                >
                                                    Spara
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {(selectedCustomer.customer_number || selectedCustomer.number) && (
                            <div className="customer-footer">
                                <div className="customer-number">
                                    Kundnummer: <strong>{selectedCustomer.customer_number || selectedCustomer.number}</strong>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default CustomerSelectionCard;
